/**
 * Jira scopes editor — the project's mapping to Jira, on the Overview tab.
 * Label = membership (the ledger); epics/fixVersions = collectors (safety nets
 * the nightly sweep stamps from); whole-board = direct membership (support).
 * Add flow fetches real boards, then searches epics within the picked board.
 */
import { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  TextField,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { Icon, SectionCard, StatusPill } from "../../mui";
import { brainTokens } from "../../design/brainTheme";
import {
  listJiraBoards,
  searchJiraEpics,
  saveProject,
  type JiraBoardRef,
  type JiraScope,
  type NamedScope,
} from "../../lib/brain-api";

const c = brainTokens.color;

const TYPE_LABELS: Record<JiraScope["type"], string> = {
  label: "label · membership",
  epic: "epic · collector",
  fixVersion: "fix version · collector",
  project: "board · membership",
};

function scopeId(s: JiraScope): string {
  if (s.type === "epic") return s.key.toUpperCase();
  if (s.type === "fixVersion") return `${s.project.toUpperCase()} ${s.version}`;
  if (s.type === "project") return s.key.toUpperCase();
  return s.label;
}

function scopeKeyOf(s: JiraScope): string {
  if (s.type === "epic") return `epic:${s.key.toUpperCase()}`;
  if (s.type === "fixVersion") return `fixVersion:${s.project.toUpperCase()}:${s.version}`;
  if (s.type === "project") return `project:${s.key.toUpperCase()}`;
  return `label:${s.label.toLowerCase()}`;
}

export function JiraScopesCard({
  projectId,
  scopes,
  legacyScope,
  canEdit,
  onChanged,
  embedded = false,
}: {
  projectId: string;
  scopes: NamedScope[] | undefined;
  /** Singular jiraScope shown when jiraScopes[] is empty (legacy records). */
  legacyScope?: JiraScope | null;
  canEdit: boolean;
  onChanged: () => void;
  /** Compact rail editor — no SectionCard chrome (Overview Record edit mode). */
  embedded?: boolean;
}) {
  const { enqueueSnackbar } = useSnackbar();
  const effective: NamedScope[] = useMemo(() => {
    if (scopes?.length) return scopes;
    if (legacyScope) return [{ name: scopeId(legacyScope), scope: legacyScope }];
    return [];
  }, [scopes, legacyScope]);

  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  // Add-dialog state
  const [type, setType] = useState<JiraScope["type"]>("epic");
  const [wsName, setWsName] = useState("");
  const [boards, setBoards] = useState<JiraBoardRef[]>([]);
  const [board, setBoard] = useState<JiraBoardRef | null>(null);
  const [epicQuery, setEpicQuery] = useState("");
  const [epicOptions, setEpicOptions] = useState<Array<{ key: string; summary: string; status: string }>>([]);
  const [epic, setEpic] = useState<{ key: string; summary: string } | null>(null);
  const [labelText, setLabelText] = useState("");
  const [versionText, setVersionText] = useState("");

  useEffect(() => {
    if (!open || boards.length) return;
    listJiraBoards()
      .then((r) => setBoards(r.boards))
      .catch(() => enqueueSnackbar("Could not fetch Jira boards.", { variant: "error" }));
  }, [open, boards.length, enqueueSnackbar]);

  useEffect(() => {
    if (!open || type !== "epic" || !board) return;
    const t = setTimeout(() => {
      searchJiraEpics(board.key, epicQuery)
        .then((r) => setEpicOptions(r.epics))
        .catch(() => setEpicOptions([]));
    }, 250);
    return () => clearTimeout(t);
  }, [open, type, board, epicQuery]);

  const write = async (next: NamedScope[]) => {
    setBusy(true);
    try {
      await saveProject({ projectId, jiraScopes: next });
      onChanged();
    } catch (e) {
      enqueueSnackbar(e instanceof Error ? e.message : "Failed to save scopes", { variant: "error" });
    } finally {
      setBusy(false);
    }
  };

  const removeScope = (target: NamedScope) =>
    void write(effective.filter((s) => scopeKeyOf(s.scope) !== scopeKeyOf(target.scope)));

  const addScope = async () => {
    let scope: JiraScope | null = null;
    if (type === "epic" && epic) scope = { type: "epic", key: epic.key };
    if (type === "label" && labelText.trim()) scope = { type: "label", label: labelText.trim().toLowerCase() };
    if (type === "fixVersion" && board && versionText.trim())
      scope = { type: "fixVersion", project: board.key, version: versionText.trim() };
    if (type === "project" && board) scope = { type: "project", key: board.key };
    if (!scope) return;
    const name = wsName.trim() || (type === "epic" && epic ? epic.summary : scopeId(scope));
    const key = scopeKeyOf(scope);
    if (effective.some((s) => scopeKeyOf(s.scope) === key)) {
      enqueueSnackbar("That scope is already on the project.", { variant: "info" });
      return;
    }
    await write([...effective, { name, scope }]);
    setOpen(false);
    setEpic(null);
    setEpicQuery("");
    setLabelText("");
    setVersionText("");
    setWsName("");
  };

  const addDialog = (
    <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontSize: 16 }}>Add Jira scope</DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "8px !important" }}>
        <TextField select size="small" label="Type" value={type} onChange={(e) => setType(e.target.value as JiraScope["type"])}>
          <MenuItem value="epic">Epic (collector — auto-labels its children)</MenuItem>
          <MenuItem value="fixVersion">Fix version (collector)</MenuItem>
          <MenuItem value="label">Label (membership ledger)</MenuItem>
          <MenuItem value="project">Whole board (direct membership — support/desk style)</MenuItem>
        </TextField>

        {(type === "epic" || type === "fixVersion" || type === "project") && (
          <Autocomplete
            size="small"
            options={boards}
            value={board}
            onChange={(_, v) => setBoard(v)}
            getOptionLabel={(b) => `${b.key} — ${b.name}`}
            renderInput={(params) => <TextField {...params} label="Board" placeholder="Pick the Jira board" />}
          />
        )}

        {type === "epic" && (
          <Autocomplete
            size="small"
            disabled={!board}
            options={epicOptions}
            value={epic as never}
            onChange={(_, v) => setEpic(v)}
            onInputChange={(_, v) => setEpicQuery(v)}
            getOptionLabel={(e) => `${e.key} — ${e.summary}`}
            filterOptions={(x) => x}
            renderInput={(params) => <TextField {...params} label="Epic" placeholder="Search epics on this board…" />}
          />
        )}

        {type === "label" && (
          <TextField size="small" label="Label" placeholder="ws-my-project" value={labelText} onChange={(e) => setLabelText(e.target.value)} />
        )}

        {type === "fixVersion" && (
          <TextField size="small" label="Version" placeholder="4.16" value={versionText} onChange={(e) => setVersionText(e.target.value)} />
        )}

        <TextField
          size="small"
          label="Workstream name (optional)"
          placeholder="Defaults to the epic title / scope id"
          value={wsName}
          onChange={(e) => setWsName(e.target.value)}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setOpen(false)}>Cancel</Button>
        <Button variant="contained" disabled={busy} onClick={() => void addScope()}>
          Add
        </Button>
      </DialogActions>
    </Dialog>
  );

  if (embedded) {
    return (
      <>
        {effective.length === 0 ? (
          <Box sx={{ fontSize: 12.5, color: c.muted2, mb: 1 }}>No scopes linked yet.</Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            {effective.map((s) => (
              <Box
                key={scopeKeyOf(s.scope)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  py: "7px",
                  borderBottom: `1px solid ${c.borderLight3}`,
                }}
              >
                <Box
                  sx={{
                    fontSize: 11,
                    color: c.tealInk,
                    background: c.tealTint,
                    borderRadius: `${brainTokens.radius.pill}px`,
                    px: "8px",
                    py: "2px",
                    flex: "none",
                  }}
                >
                  {s.scope.type}
                </Box>
                <Box sx={{ fontFamily: brainTokens.font.mono, fontSize: 11.5, color: c.ink3, flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {scopeId(s.scope)}
                </Box>
                {canEdit && (
                  <IconButton size="small" disabled={busy} onClick={() => removeScope(s)} title="Remove scope" sx={{ color: c.faint }}>
                    <Icon name="x" size={14} />
                  </IconButton>
                )}
              </Box>
            ))}
          </Box>
        )}
        {canEdit && (
          <Button size="small" startIcon={<Icon name="plus" size={13} />} onClick={() => setOpen(true)} sx={{ mt: "10px" }}>
            Add
          </Button>
        )}
        {addDialog}
      </>
    );
  }

  return (
    <SectionCard
      label="Jira scopes"
      actions={
        canEdit ? (
          <Button size="small" startIcon={<Icon name="plus" size={13} />} onClick={() => setOpen(true)}>
            Add scope
          </Button>
        ) : undefined
      }
    >
      {effective.length === 0 ? (
        <Box sx={{ fontSize: 13, color: c.muted2 }}>
          No Jira mapping yet — add the epics this project's work is filed under (any board), and the
          nightly sweep will keep its label in sync.
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
          {effective.map((s) => (
            <Box
              key={scopeKeyOf(s.scope)}
              sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.5, borderBottom: `1px solid ${c.borderLight}`, "&:last-child": { borderBottom: "none" } }}
            >
              <Box sx={{ fontSize: 13.5, fontWeight: 600, color: c.ink, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {s.name}
              </Box>
              <StatusPill tone={s.scope.type === "label" || s.scope.type === "project" ? "teal" : "neutral"} label={TYPE_LABELS[s.scope.type]} />
              <Box sx={{ fontFamily: brainTokens.font.mono, fontSize: 12.5, color: c.muted, flex: 1 }}>
                {s.scope.type === "epic" || s.scope.type === "project" ? (
                  <Box
                    component="a"
                    href={`https://aeronetnz.atlassian.net/browse/${scopeId(s.scope)}`}
                    target="_blank"
                    rel="noopener"
                    sx={{ color: c.tealInk, textDecoration: "none", "&:hover": { textDecoration: "underline" } }}
                  >
                    {scopeId(s.scope)}
                  </Box>
                ) : (
                  scopeId(s.scope)
                )}
              </Box>
              {canEdit && (
                <IconButton size="small" disabled={busy} onClick={() => removeScope(s)} title="Remove scope">
                  <Icon name="x" size={13} />
                </IconButton>
              )}
            </Box>
          ))}
        </Box>
      )}
      {addDialog}
    </SectionCard>
  );
}
